import * as React from "react";
import { MDXRemoteSerializeResult } from "next-mdx-remote";
import Link from "next/link";
import { BookOpenText, MoveLeft } from "lucide-react";
import { useRouter } from "next/router";
import readingTime from "reading-time";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import { getAllPosts, getPostBySlug } from "@/lib/posts";
import {
  CopyButton,
  MdxContent,
  Page,
  Tooltip,
  DotBackground,
  GridBackground,
  MaskDotBackground,
  Code,
  MetallicButton,
  MetallicButtonList,
  HeroBg,
} from "@/components";
import { XIcon } from "@/components/Icons";
import { BlogPostMetaData } from "@/types";
import { TransitionContext } from "@/contexts/TransitionContext";

const components = {
  code: (props: any) => <Code {...props} />,
  GridBackground,
  DotBackground,
  MaskDotBackground,
  MetallicButton,
  MetallicButtonList,
};

/**
 * Fetches the static props for the blog post page.
 *
 * @param params The parameters for the static props.
 * @returns The static props for the blog post page.
 */
export async function getStaticProps({ params }: { params: { slug: string } }) {
  const { slug } = params;
  const post = await getPostBySlug(slug);

  return { props: { content: post.content, metadata: post.metadata } };
}

/**
 * Fetches the static paths for the blog post pages.
 *
 * @returns The static paths for the blog post pages.
 */
export async function getStaticPaths() {
  const blogPosts = await getAllPosts();
  const paths = blogPosts.map((blog) => ({
    params: {
      slug: blog.metadata?.slug,
    },
  }));

  return { paths, fallback: false };
}

type BlogPostProps = {
  content: MDXRemoteSerializeResult<
    Record<string, unknown>,
    Record<string, unknown>
  >;
  metadata: BlogPostMetaData;
};

/**
 * Component that renders a blog post.
 *
 * @param content The content of the blog post.
 * @param metadata The metadata of the blog post.
 */
const BlogPost: React.FC<BlogPostProps> = ({ content, metadata }) => {
  const container = React.useRef<HTMLDivElement>(null);

  const { timeline } = React.useContext(TransitionContext);

  useGSAP(
    () => {
      gsap.fromTo(container.current, { opacity: 0 }, { opacity: 1 });
      timeline.add(gsap.to(container.current, { opacity: 0 }));
    },
    { scope: container }
  );

  const articleStyles = `
    prose lg:prose-lg xl:prose-2xl 2xl:prose-3xl prose-headings:text-primary-50 prose-p:text-primary-400/85 prose-headings:font-Acorn-Bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-h4:text-lg prose-h5:text-base prose-h6:text-sm prose-invert prose-a:text-primary-100/90 prose-a:active:cursor-active prose-a:cursor-pointer prose-p:text-[clamp(1rem,.5952rem+.3898vw,1.375rem)] prose-p:!leading-[2] prose-li:marker:text-primary-400 prose-li:marker:text-2xl max-w-none prose-headings:mt-10
  `;

  const router = useRouter();

  const getBlogShareUrl = () => {
    const baseUrl =
      process.env.NODE_ENV === "development"
        ? "http://localhost:3000"
        : "https://tarunsharma.dev";

    const link = `${baseUrl}${router.asPath}`;

    return link;
  };

  return (
    <Page pageTitle={metadata.title}>
      <section
        ref={container}
        className="relative gap-y-20 px-0 pb-16 pt-32 sm:px-10"
      >
        <HeroBg />
        <div className="noise-overlay xs:!opacity-0 absolute inset-0 !z-0 h-[70%] w-full !opacity-50" />
        <div className="ring-primary-500/50 from-primary-950 via-primary-950/70 to-primary-950/65 3xl:max-w-6xl 4xl:max-w-7xl mx-auto mb-14 w-full max-w-4xl rounded-[3rem] bg-gradient-to-t px-5 py-20 shadow-lg ring-1 backdrop-blur-3xl sm:rounded-2xl sm:px-10 2xl:max-w-5xl">
          <div className="mb-10 flex w-full items-center justify-between">
            <Link
              scroll={false}
              href="/blogs"
              className="text-primary-50 active:cursor-active hover:text-primary-200 xs:text-base flex cursor-pointer items-center gap-x-1.5 text-sm transition"
            >
              <MoveLeft className="h-4 w-4" />
              <span>Back to all blogs</span>
            </Link>

            <div className="flex items-center justify-center gap-x-3">
              <Tooltip text="Copy link">
                <CopyButton
                  text={getBlogShareUrl()}
                  className="bg-primary-600/60 hover:bg-primary-600 relative z-10 flex h-8 w-8 items-center justify-center rounded-full p-1.5 transition"
                  aria-label="Copy blog post link"
                />
              </Tooltip>
              <Tooltip text="Share on twitter">
                <a
                  target="_blank"
                  href={`https://twitter.com/intent/tweet?text=${
                    metadata.title
                  } by @tarunfy&url=${getBlogShareUrl()}`}
                  className="bg-primary-600/60 hover:bg-primary-600 active:cursor-active relative z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full p-1.5 transition"
                  aria-label="Share on twitter"
                >
                  <XIcon />
                </a>
              </Tooltip>
            </div>
          </div>
          <div className="border-primary-50/20 mb-10 flex flex-col items-center gap-y-4 border-b pb-5">
            <h1 className="text-primary-50 font-Acorn-Bold max-w-2xl text-center !text-[clamp(2.3rem,.5692rem+1.6vw,13.75rem)] !leading-[1.25] 2xl:max-w-3xl">
              {metadata.title}
            </h1>
            <div className="flex items-center justify-center gap-x-2">
              <span className="text-primary-400/70 flex items-center">
                <BookOpenText className="mr-2 h-4 w-4" />
                {readingTime(content.compiledSource).text}
              </span>
              <div className="mx-1">
                <span className="text-primary-400/90 text-2xl">/</span>
              </div>
              <div className="text-primary-400/70">{metadata.publishedAt}</div>
            </div>
          </div>
          <article className={articleStyles}>
            <MdxContent {...content} components={components} />
          </article>
        </div>
      </section>
    </Page>
  );
};

export default BlogPost;
